import React, { FC, useEffect } from 'react'
import type { Dispatch } from 'redux'
import { connect } from 'react-redux'
import * as Icons from '@revolut/icons'
import {
  Box,
  Circle,
  Collapsible,
  H1,
  Media,
  Text,
  TextBox,
  TextButton,
} from '@revolut/ui-kit'
import type { AccessToken, Error, AccountsConsent } from 'types/common'
import { fetchPocketsAction } from 'thunks'
import { accountPermissions, FetchStates, Screens } from 'constants/common'
import type { PocketsState } from 'reducers/pocketsReducer'
import { setActiveScreenAction, setErrorAction } from 'reducers/generalReducer'
import type { GeneralState } from 'reducers/generalReducer'
import type { AppState } from 'store'
import { CardSlide } from 'components/CardSlide'
import { PocketGroup } from './PocketGroup'
import { calculateConsentExpirationDate } from '../../helpers/common'
import { ApplicationData } from '../../services/api'

type PocketsProps = {
  applicationData: ApplicationData
  fetchPockets: (accessToken: AccessToken) => void
  setError: (errorData: Error) => void
  setActiveScreen: (screen: Screens) => void
  handleSubmit: () => void
  setFieldValue: (field: string, value?: any) => void
  onBack: () => void
  values: {
    pockets: string[]
  }
  errors: {
    pockets: string
  }
  pockets: PocketsState
  general: GeneralState<AccountsConsent>
}

const getPermissionTextList = (permissions: (keyof typeof accountPermissions)[]) =>
  permissions.map((permission) => (
    <Media mt='1rem' key={permission}>
      <Media.Side mr='1rem'>
        <Icons.Check size='1.5rem' color='grey-80' />
      </Media.Side>
      <Media.Content flex='1'>{accountPermissions[permission] || ''}</Media.Content>
    </Media>
  ))

const Pockets: FC<PocketsProps> = ({
  applicationData,
  setFieldValue,
  setError,
  setActiveScreen,
  handleSubmit,
  fetchPockets,
  values,
  pockets,
  general: {
    data: {
      accessToken,
      consent: {
        details: { pockets: consentedPocketIds = [], permissions, expiryDate },
      },
    },
  },
  ...rest
}) => {
  useEffect(() => {
    fetchPockets(accessToken)
  }, [fetchPockets, accessToken])

  useEffect(() => {
    if (pockets.fetchState === FetchStates.SUCCESS) {
      if (consentedPocketIds.length) {
        const matchingPockets = consentedPocketIds.map((id: string) => id)
        const usersPockets = pockets.data.some(
          (pocket) => pocket.state === 'ACTIVE' && matchingPockets.includes(pocket.id),
        )
        if (!usersPockets) {
          setError({
            message: 'Account not found',
            description: 'Please double check your account number and sort code.',
          })
          setActiveScreen(Screens.ERROR)
        }

        // https://github.com/jaredpalmer/formik/issues/930
        setTimeout(() => {
          setFieldValue('pockets', consentedPocketIds.concat([]))
        })
      } else {
        setTimeout(() =>
          setFieldValue(
            'pockets',
            pockets.data
              .filter(({ state }) => state === 'ACTIVE')
              .map(({ id }: { id: string }) => id),
          ),
        )
      }
      pockets.data.forEach((pocket) => {
        if (pocket.type === 'CREDIT') {
          pocket.balance = -pocket.balance
        }
      })
    }
  }, [
    consentedPocketIds,
    pockets.data,
    pockets.fetchState,
    setActiveScreen,
    setError,
    setFieldValue,
  ])

  const { expiresAfter, expiresAtString } = calculateConsentExpirationDate(expiryDate)

  const hasApprovedPockets = !!consentedPocketIds.length
  const title = `Accounts access request`
  const buttonText = 'Authorise'
  return (
    <CardSlide
      isLoading={FetchStates.PENDING === pockets.fetchState}
      onSubmit={handleSubmit}
      buttonText={hasApprovedPockets ? 'Refresh token' : buttonText}
      header={
        <>
          <H1>{title}</H1>
          <TextBox color='grey-50' mt='0.75rem' mb='1rem'>
            Authorise{' '}
            <Text color='black' fontWeight={500}>
              {applicationData.name}
            </Text>{' '}
            to read your accounts information. Permission will expire on {expiresAtString}{' '}
            (in {expiresAfter} days).
          </TextBox>
        </>
      }
      bottom={
        <Box width='100%'>
          <Collapsible
            renderToggle={({ isOpen, toggle }) => (
              <Media>
                <Media.Content>
                  <Text color='black' fontWeight={500}>
                    Info I’m providing
                  </Text>
                </Media.Content>
                <Media.Side>
                  <TextButton
                    onClick={() => toggle(!isOpen)}
                    width='100%'
                    textAlign='center'
                  >
                    {isOpen ? 'Hide' : 'Show'}
                  </TextButton>
                </Media.Side>
              </Media>
            )}
          >
            <Box color='grey-50'>{getPermissionTextList(permissions)}</Box>
          </Collapsible>
        </Box>
      }
      {...rest}
    >
      {FetchStates.SUCCESS === pockets.fetchState && (
        <Box bg='white' mb='1.5rem'>
          <PocketGroup
            consentedPocketIds={consentedPocketIds}
            pockets={pockets.data}
            values={values.pockets}
            type='CURRENT'
            groupHeaderText='Choose accounts'
            approvedGroupHeaderText='Previously approved accounts'
          />
          <PocketGroup
            consentedPocketIds={consentedPocketIds}
            pockets={pockets.data}
            values={values.pockets}
            type='CREDIT'
            groupHeaderText='Credit'
            approvedGroupHeaderText='Previously approved credit'
            showName
            icon={
              <Circle bg='transparent-teal' size='icon.48'>
                <Icons.Credit color='teal' />
              </Circle>
            }
          />
          <PocketGroup
            consentedPocketIds={consentedPocketIds}
            pockets={pockets.data}
            values={values.pockets}
            type='SAVINGS'
            groupHeaderText='Vaults'
            approvedGroupHeaderText='Previously approved vaults'
            showName
            icon={
              <Circle bg='transparent-orange' size='icon.48'>
                <Icons.Safe color='orange' />
              </Circle>
            }
          />
        </Box>
      )}
    </CardSlide>
  )
}

const mapStateToProps = ({ pockets, general }: AppState) => ({
  pockets,
  general,
})

const mapDispatchToProps = (dispatch: Dispatch<any>) => ({
  fetchPockets: (accessToken: AccessToken) => dispatch(fetchPocketsAction(accessToken)),
  setError: ({ message, description }: Error) =>
    dispatch(setErrorAction({ message, description })),
  setActiveScreen: (screen: Screens) => dispatch(setActiveScreenAction(screen)),
})

const ConnectedPockets = connect(mapStateToProps, mapDispatchToProps)(Pockets)

export { ConnectedPockets as Pockets }
